<template>
  <div
    v-if="hasCertificate"
    class="order-certificate-summary"
  >
    <div class="summary-header">
      <div class="summary-copy">
        <span class="summary-badge">
          <v-icon
            small
            class="summary-badge-icon"
          >
            mdi-shield-check
          </v-icon>
          {{ $t('components.order_certificate_summary.available') }}
        </span>
        <div class="summary-date">
          {{ $t('components.order_certificate_summary.created_at') }}
          <span class="summary-date-value">
            {{ order.updated_at }}
          </span>
        </div>
      </div>

      <v-btn
        v-if="showButton"
        class="summary-button"
        color="primary"
        @click="showCertificate"
      >
        <v-icon left>
          mdi-file-document-outline
        </v-icon>
        {{ $t(buttonTextKey) }}
      </v-btn>
    </div>

    <div class="summary-cards">
      <div
        v-if="materialSummary.length"
        class="summary-card-group"
      >
        <div
          v-for="(material, index) in materialSummary"
          :key="material.key"
          class="summary-card-entry"
        >
          <div
            class="summary-card-item"
          >
            <span class="summary-card-name">
              {{ material.label }}
            </span>
            <span class="summary-card-value">
              {{ material.value }}
            </span>
          </div>
          <v-divider
            v-if="index < materialSummary.length - 1"
            class="summary-card-divider"
          />
        </div>
      </div>

      <div
        v-if="detailsSummary.length"
        class="summary-card-group"
      >
        <div
          v-for="(detail, index) in detailsSummary"
          :key="detail.key"
          class="summary-card-entry"
        >
          <div
            class="summary-card-item"
          >
            <span class="summary-card-name">
              {{ detail.label }}
            </span>
            <span class="summary-card-value">
              {{ detail.value }}
            </span>
          </div>
          <v-divider
            v-if="index < detailsSummary.length - 1"
            class="summary-card-divider"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MaterialTypeKey } from '@/enums/MaterialType';

const materialTypes = Object.values(MaterialTypeKey);

export default {
  name: 'OrderCertificateSummary',
  props: {
    order: {
      type: Object,
      default: () => {},
    },
    showButton: {
      type: Boolean,
      default: true,
    },
    buttonTextKey: {
      type: String,
      default: 'views.order.show_certificate',
    },
  },
  computed: {
    hasCertificate() {
      return !!this.order?.certify?.url;
    },
    materialSummary() {
      return materialTypes.reduce((summary, type) => {
        const quantity = this.order?.material_quantity?.[type]?.quantity;

        if (Number(quantity) > 0) {
          summary.push({
            key: type,
            label: this.$t(`material_type.${type}`),
            value: this.$t('components.order_certificate_summary.material_amount', {
              quantity,
            }),
          });
        }

        return summary;
      }, []);
    },

    orderStates() {
      const states = this.order?.credit_offereds?.map((creditOffered) => creditOffered.business_state) || [];

      return [...new Set(states.filter(Boolean))].sort();
    },

    orderStatesLabel() {
      return this.orderStates.join(', ');
    },

    nfeYears() {
      const years = this.order?.credit_offereds?.flatMap((creditOffered) => creditOffered.year_nfes || []) || [];

      return [...new Set(years.filter(Boolean))].sort((firstYear, secondYear) => secondYear - firstYear);
    },

    nfeYearsLabel() {
      return this.nfeYears.join(', ');
    },

    detailsSummary() {
      const cards = [];

      if (this.orderStatesLabel) {
        cards.push({
          key: 'order-states',
          label: this.$t('components.order_certificate_summary.order_states'),
          value: this.orderStatesLabel,
        });
      }

      if (this.nfeYearsLabel) {
        cards.push({
          key: 'nfe-years',
          label: this.$t('components.order_certificate_summary.nfe_year'),
          value: this.nfeYearsLabel,
        });
      }

      return cards;
    },
  },
  methods: {
    showCertificate() {
      window.open(this.order.certify.url);
    },
  },
};
</script>

<style lang="scss" scoped>
  .order-certificate-summary {
    background-color: rgba($color_primary, 0.08);
    border: 1px solid rgba($color_primary, 0.16);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;

    .summary-header {
      align-items: center;
      display: flex;
      gap: 16px;
      justify-content: space-between;
    }

    .summary-copy {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .summary-badge {
      align-items: center;
      color: $color_primary;
      display: inline-flex;
      font-size: 1em;
      font-weight: bold;
      gap: 8px;
      width: fit-content;
    }

    .summary-badge-icon {
      color: inherit;
      margin-bottom: 1px;
    }

    .summary-date {
      color: rgba($color_dark, 0.72);
      font-size: 0.95em;
      font-weight: bold;
    }

    .summary-date-value {
      color: $color_dark;
      font-weight: normal;
      margin-left: 4px;
    }

    .summary-button {
      border-radius: 10px;
      height: 50px;
      min-width: 240px;
    }

    ::v-deep .summary-button .v-btn__content {
      font-size: 1em;
      font-weight: 600;
      letter-spacing: 0;
      text-transform: none;
    }

    .summary-cards {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }

    .summary-card-group {
      background-color: $color_light;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      min-height: 96px;
      min-width: 220px;
      overflow: hidden;
    }

    .summary-card-entry {
      display: flex;
      flex-direction: column;
    }

    .summary-card-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
      justify-content: center;
      padding: 12px 14px;
    }

    .summary-card-divider {
      margin: 0 14px;
    }

    .summary-card-name {
      color: rgba($color_dark, 0.72);
      font-size: 0.875em;
      font-weight: bold;
    }

    .summary-card-value {
      color: $color_dark;
      font-weight: bold;
    }
  }
</style>
